import { useWatch } from 'react-hook-form';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { useFormContext } from 'app/hooks/form';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigation } from 'app/hooks/navigation';
import { useCreateListing } from 'app/network/listing';
import { hasNotificationPermission } from 'app/lib/notifications';
import { fileTooLargeMessage, isFileTooLargeError } from 'app/lib/upload-errors';
import { RemoteError } from 'app/design/primitives/remote-error';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { hasSignal, Signal } from 'app/network/signals';
import { useTrialFreeSubAvailable } from 'app/network/promotions';
import Toast from 'react-native-toast-message';
import { type CreateListingDTO, ListingType } from 'app/types/listing';
import { isPublicServiceCategory } from 'app/network/category';
import { useCreationTab } from '../creation-tab-context';
import { useState } from 'react';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useUser } from 'app/network/user';
import { isMobileNative, useUISize } from 'app/utils/web-helpers';
import { Signal as SignalIcon } from 'app/design/icons/signal';
import { createTheme as createDSTheme } from 'app/shopify';
import { usePresentAppDownloadPrompt } from 'app/hooks/use-present-app-download-prompt';
const theme = createDSTheme();

function NewListingSubmitContent({
  conciergerieEnabled,
  duplicatesCount,
  showAssistantMessageBottomSheet,
  showConciergerieFlag,
  type,
}: {
  conciergerieEnabled: boolean;
  duplicatesCount: number;
  showAssistantMessageBottomSheet: boolean;
  showConciergerieFlag: boolean;
  type: CreateListingDTO['type'];
}) {
  const { t } = useScopedTranslation();
  const totalOffers = Math.max(1, duplicatesCount + 1);
  const showAssistantIcon = showConciergerieFlag && conciergerieEnabled && type === ListingType.request;
  const label =
    type === ListingType.offer
      ? duplicatesCount > 0
        ? t('primaryButtonPostOfferMultiple', { count: totalOffers })
        : t('primaryButtonPostOffer')
      : showConciergerieFlag && conciergerieEnabled && showAssistantMessageBottomSheet
        ? t('primaryButtonConfirmRequest')
        : t('primaryButtonPostRequest');

  return (
    <>
      {showAssistantIcon ? <SignalIcon height={20} width={20} color="white" style={{ marginRight: 8 }} /> : null}
      <Text color="surface.default" textAlign="center">
        {label}
      </Text>
    </>
  );
}

export function Actions({
  conciergerieEnabled = true,
  onListingCreated,
  onPublishingChange,
  setShowAssistantMessageBottomSheet,
  showAssistantMessageBottomSheet,
}: {
  conciergerieEnabled?: boolean;
  onListingCreated?: () => void;
  onPublishingChange?: (pending: boolean) => void;
  setShowAssistantMessageBottomSheet: (value: boolean) => void;
  showAssistantMessageBottomSheet: boolean;
}) {
  const { t } = useScopedTranslation();
  const { t: tAPI } = useTranslation('api-errors');
  const navigation = useNavigation<'new'>();
  const creationTab = useCreationTab();
  const { data: currentUser, isFetched: isUserFetched } = useUser();
  const showConciergerieFlag =
    isUserFetched && !!currentUser && isFeatureEnabled(FeatureFlag.conciergerie, currentUser.featureFlags);
  const { clearErrors, handleSubmit, control, getValues, setError } = useFormContext<CreateListingDTO>(); // Workaround for https://github.com/react-hook-form/react-hook-form/issues/12916

  const serviceCategoryId = useWatch({ control, name: 'serviceCategoryId' });
  const isPublicServiceRequest = isPublicServiceCategory(serviceCategoryId);

  const { isLargeUI } = useUISize();
  const presentAppDownloadPrompt = usePresentAppDownloadPrompt();

  const trialFreeSubAvailable = useTrialFreeSubAvailable(navigation);

  const { error, mutate, isPending } = useCreateListing({
    onSettled: () => onPublishingChange?.(false),
    async onSuccess(listing) {
      onListingCreated?.();
      const { shouldShowFreeSubScreen, navigateToFreeSubScreen } = trialFreeSubAvailable(listing);
      if (shouldShowFreeSubScreen) {
        navigation.navigate('billboard', {
          initial: true,
          screen: 'all-listings',
        });
        // Use setTimout to run this only once the screen animation (from above navigation) completes
        setTimeout(navigateToFreeSubScreen, 10);
      } else {
        const fromNewScreen = true;
        const userNowContributor = hasSignal(listing, Signal.UserLevelNowContributor);
        if (userNowContributor) {
          if (!creationTab) navigation.goBack();
          navigation.navigate('billboard', {
            initial: true,
            screen: 'all-listings',
            params: { userNowContributor },
          });
        } else {
          if (creationTab) {
            navigation.navigate('listingModal', { screen: 'one-listing', params: { id: listing.id, fromNewScreen } });
          } else if (isLargeUI) {
            navigation.goBack();
          } else {
            // Replace the 'new' modal with the listing screen in a single animation
            navigation.replace('listingModal', {
              screen: 'one-listing',
              params: { id: listing.id, fromNewScreen },
            });
          }
          // Show a Toast
          setTimeout(() => {
            Toast.show({
              type: 'success',
              position: 'bottom',
              bottomOffset: theme.spacing['spacing.general.x8'],
              text1: t('listingCreatedToast.title', { ns: 'new' }),
              text2: t(`listingCreatedToast.text.${listing.type}`, { ns: 'new' }),
              visibilityTime: 15000,
            });
          }, 400);
        }
      }

      presentAppDownloadPrompt({ context: 'listing-published' });

      const hasGrantedNotificationPermission = await hasNotificationPermission();

      const shouldAskForNotificationPermission = !hasGrantedNotificationPermission && isMobileNative;

      if (shouldAskForNotificationPermission) {
        setTimeout(
          () =>
            navigation.navigate(
              'notification-explainer',
              {
                context: 'listing',
              },
              { pop: true },
            ),
          1500,
        );
      }
    },
  });

  const startDate = useWatch({ control, name: 'start' });
  const [validationState, setValidationState] = useState<{ startDate: typeof startDate; error: Error | null }>(() => ({
    startDate,
    error: null,
  }));
  let currentValidationState = validationState;
  if (validationState.startDate !== startDate) {
    currentValidationState = { startDate, error: null };
    setValidationState(currentValidationState);
  }
  const validationError = currentValidationState.error;

  const onSubmit = handleSubmit(
    (form) => {
      // Make date required if it's a public service request
      if (isPublicServiceRequest) {
        if (getValues('start') === null) {
          setValidationState({ startDate, error: new Error('Date is required') });
          return;
        }
      }
      const shouldUseConciergerie = form.type === ListingType.request && showConciergerieFlag && conciergerieEnabled;
      if (!showAssistantMessageBottomSheet && shouldUseConciergerie) {
        clearErrors('automaticMessage');
        setShowAssistantMessageBottomSheet(true);
        return;
      }
      if (shouldUseConciergerie && !form.automaticMessage?.trim()) {
        setError('automaticMessage', { type: 'required' });
        return;
      }
      onPublishingChange?.(true);
      mutate(
        form.type === ListingType.request && showConciergerieFlag && !conciergerieEnabled
          ? { ...form, automaticMessage: null }
          : form,
      );
    },
    (err) => console.log('[DEV] Form validation error(s):', err),
  );

  const anyError = validationError || error;

  const errorMessage = anyError
    ? isFileTooLargeError(anyError)
      ? fileTooLargeMessage(anyError)
      : tAPI(anyError.message)
    : undefined;

  const watchedType = useWatch({ control, name: 'type' });
  const watchedDuplicates = useWatch({ control, name: 'duplicates' });
  const duplicatesCount = typeof watchedDuplicates === 'number' ? watchedDuplicates : 0;

  return (
    <>
      {anyError ? (
        <View style={{ marginBottom: 20 }}>
          <RemoteError {...{ error: anyError, message: errorMessage }} />
        </View>
      ) : null}

      <Button
        loading={isPending || !isUserFetched}
        onPress={() => void onSubmit()}
        testID="new-listing-submit-button"
        variant="primary"
      >
        <NewListingSubmitContent
          conciergerieEnabled={conciergerieEnabled}
          duplicatesCount={duplicatesCount}
          showAssistantMessageBottomSheet={showAssistantMessageBottomSheet}
          showConciergerieFlag={showConciergerieFlag}
          type={watchedType}
        />
      </Button>
    </>
  );
}
